import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Checkbox,
  useTheme,
  Divider,
  Grid,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Close,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  InsertLink,
  AttachFile,
  Send,
  Fullscreen,
  FullscreenExit,
  KeyboardVoice,
} from "@mui/icons-material";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { useModalStyles } from "../../common/useModalStyles";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useForm } from "react-hook-form";
import {
  fetchAgenciesList,
  setSingleAlertObj,
  SingleAlertInfo,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  fetchManufacturerList,
  SearchManufacturerRequest,
  useManufacturerListSlice,
} from "../../../redux/slice/manufacturer/ManufacturerSlice";
import {
  getPracticesByUserRole,
  usePracticeSlice,
} from "../../../redux/slice/practiceSlice";
import {
  fetchAllUserRoles,
  fetchUsersByUserInput,
  fetchUsersByUserRoleIds,
  useUserReducer,
} from "../../../redux/slice/userSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import {
  NOTIFICATION_TARGET_ENTITY_NAMES,
  USER_ROLE_ENTITY_NAMES,
  USER_ROLES,
} from "../../../constants/applicationConstants";
import SpeechToTextPopup from "../../common/SpeechToTextPopup";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import CcBccField, { RecipientOption } from "./CcBccField";

type ComposeType = {
  toUser: string;
  toGroup: string;
  toEntity: number[];
  subject: string;
};

interface ComposePopupProps {
  mode: "group" | "user" | "reply";
  parentNotification: any;
  onSend: (
    toValue: string,
    entity: number[],
    subject: string,
    message: string,
    important: boolean,
    attachments: Attachment[],
    parentId?: number,
    cc?: number[],
    bcc?: number[]
  ) => void;
  onClose: () => void;
  onRemoveAttachments?: () => void,
  onAttachFiles?: (files: Attachment[]) => Promise<void> | void;
  onRemoveAttachment?: (file: Attachment) => void;
}

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

interface Attachment {
  tempId: string
  documentName: string;
  content: string;
  fileType: string;
}

const ComposePopup: React.FC<ComposePopupProps> = ({
  mode,
  parentNotification,
  onSend,
  onClose,
  onAttachFiles,
  onRemoveAttachment,
  onRemoveAttachments
}) => {
  const theme = useTheme();
  const { tokens: mTokens, isDark, primaryBtnSx, closeIconSx } = useModalStyles();
  const [to, setTo] = useState<number>(0);
  const [subject, setSubject] = useState("");
  const [important, setImportant] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [toRecipients, setToRecipients] = useState<RecipientOption[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [ccRecipients, setCcRecipients] = useState<RecipientOption[]>([]);
  const [bccRecipients, setBccRecipients] = useState<RecipientOption[]>([]);

  const { control, watch, getValues, setValue, reset } = useForm<ComposeType>();
  const dispatch = useAppDispatch();
  const { ManufacturerListInfo } = useManufacturerListSlice();
  const { fetchUsersByQuery, userListByRoleIds, userRoles } = useUserReducer();
  const { practiceByUserRole } = usePracticeSlice();
  const [selectedGroup, setSelectedGroup] = React.useState<string>("");
  const [selectedEntity, setSelectedEntity] = React.useState<number[]>([]);
  const [entityOptions, setEntityOptions] = React.useState<
    { value: number; label: string }[]
  >([]);
  const { userInfo } = useAuthSlice();
  const [groupOptions, setGroupOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [disableGroupField, setDisableGroupField] = useState<boolean>(false);
  const [disableEntityField, setDisableEntityField] = useState<boolean>(false);
  const { agencyInfoList } = useCommonReducer();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isVoicePopupOpen, setIsVoicePopupOpen] = useState(false);
  const [searchCriteria, setSearchCriteria] =
    useState<SearchManufacturerRequest>({
      mfgIds: [],
      contactPhone: "",
      currentPage: 1,
      itemsPerPage: 500,
      manufacturerStatus: [],
      activeOnly: true,
    });
  const [entityLabel, setEntityLabel] = useState<String>("Entity");

  const userOptions: RecipientOption[] = React.useMemo(() => {
    if (mode !== "user" || !fetchUsersByQuery) return [];
    return fetchUsersByQuery.map((item: any) => ({
      value: parseInt(item.userId),
      label: `${item.firstName} ${item.middleName ? item.middleName + " " : ""}${item.lastName} (${item.email})`,
    }));
  }, [fetchUsersByQuery, mode]);

  useEffect(() => {
    setEntityOptions([]);
    setGroupOptions([]);
    setToRecipients([]);
    setShowCc(false);
    setShowBcc(false);
    setCcRecipients([]);
    setBccRecipients([]);
    reset({ toGroup: "" });

    if (mode == "user") {
      dispatch(fetchUsersByUserInput({ searchQuery: "", skipLoader: true }));
    } else if (mode == "group") {
      dispatch(fetchAllUserRoles());
      if (
        userInfo.userRole === USER_ROLES.ROLE_ADMIN ||
        userInfo.userRole === USER_ROLES.ROLE_SUPERADMIN
      ) {
        setGroupOptions([
          {
            value: NOTIFICATION_TARGET_ENTITY_NAMES.EVERYONE,
            label: "Everyone",
          },
          {
            value: NOTIFICATION_TARGET_ENTITY_NAMES.MANUFACTURER,
            label: "Manufacturer",
          },
          {
            value: NOTIFICATION_TARGET_ENTITY_NAMES.PRACTICE,
            label: "Practice",
          },
          { value: NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN, label: "Admin" },
          { value: NOTIFICATION_TARGET_ENTITY_NAMES.AGENCY, label: "Agency" },
          {
            value: NOTIFICATION_TARGET_ENTITY_NAMES.SUPER_ADMIN,
            label: "Super Admin",
          },
        ]);
      } else {
        if (userInfo.entityName === NOTIFICATION_TARGET_ENTITY_NAMES.PRACTICE) {
          setGroupOptions([
            {
              value: NOTIFICATION_TARGET_ENTITY_NAMES.PRACTICE,
              label: "Practice",
            },
            { value: NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN, label: "Admin" },
          ]);
        } else if (
          userInfo.entityName === USER_ROLE_ENTITY_NAMES.MANUFACTURER
        ) {
          setGroupOptions([
            {
              value: NOTIFICATION_TARGET_ENTITY_NAMES.MANUFACTURER,
              label: "Manufacturer",
            },
            { value: NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN, label: "Admin" },
          ]);
        } else if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY) {
          setGroupOptions([
            {
              value: NOTIFICATION_TARGET_ENTITY_NAMES.AGENCY,
              label: "Agency",
            },
            { value: NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN, label: "Admin" },
          ]);
        }
      }
    } else {
      if (parentNotification) {
        setEntityOptions([
          {
            value: parentNotification.senderId,
            label: parentNotification.senderName,
          },
        ]);
        setSubject("Reply");
      }
    }
  }, [mode]);

  useEffect(() => {
    const toGroup = watch("toGroup");
    setDisableEntityField(false);
    setSelectedGroup(toGroup);
    setValue("toEntity", []);
    if (toGroup == NOTIFICATION_TARGET_ENTITY_NAMES.MANUFACTURER) {
      setEntityLabel("Manufacturer");
      dispatch(fetchManufacturerList(searchCriteria));
    } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.PRACTICE) {
      setEntityLabel("Practice");
      dispatch(getPracticesByUserRole(true));
    } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN) {
      setEntityLabel("Admin");
      if (userRoles) {
        const adminRoleId = userRoles.find(
          (role: any) => role.roleName === USER_ROLES.ROLE_ADMIN
        )?.userRoleId;
        dispatch(
          fetchUsersByUserRoleIds({ userRoleIds: [Number(adminRoleId)] })
        );
      }
    } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.AGENCY) {
      setEntityLabel("Agency");
      dispatch(fetchAgenciesList());
    } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.SUPER_ADMIN) {
      setEntityLabel("Super Admin");
      if (userRoles) {
        const superAdminRoleId = userRoles.find(
          (role: any) => role.roleName === USER_ROLES.ROLE_SUPERADMIN
        )?.userRoleId;
        dispatch(
          fetchUsersByUserRoleIds({ userRoleIds: [Number(superAdminRoleId)] })
        );
      }
    } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.EVERYONE) {
      setEntityLabel("Entity");
      setEntityOptions([]);
      setDisableEntityField(true);
    } else if (toGroup === "") {
      setEntityLabel("Entity");
    }
  }, [watch("toGroup"), userRoles]);

  useEffect(() => {
    const toGroup = watch("toGroup");
    if (mode == "group") {
      if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.MANUFACTURER) {
        if (
          ManufacturerListInfo &&
          ManufacturerListInfo?.totalElements > 0
        ) {
          const options = ManufacturerListInfo.content
            ?.map((item) => ({
              value: item.manufacturerId,
              label: item.manufacturerName,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          if (ManufacturerListInfo.totalElements > 1) {
            setEntityOptions([
              { value: -1, label: "All Manufacturers" },
              ...options,
            ]);
          } else {
            setEntityOptions(options);
          }
        }
      } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.PRACTICE) {
        if (practiceByUserRole && practiceByUserRole?.length > 0) {
          const options = practiceByUserRole
            ?.map((item) => ({
              value: parseInt(item.practiceId),
              label: item.practiceName,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          if (practiceByUserRole.length > 1) {
            setEntityOptions([
              { value: -1, label: "All Practices" },
              ...options,
            ]);
          } else {
            setEntityOptions(options);
          }
        }
      } else if (toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.AGENCY) {
        if (agencyInfoList && agencyInfoList?.length > 0) {
          const options = agencyInfoList
            ?.map((item) => ({
              value: parseInt(item.agencyId),
              label: item.agencyName,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          if (agencyInfoList.length > 1) {
            setEntityOptions([
              { value: -1, label: "All Agencies" },
              ...options,
            ]);
          } else {
            setEntityOptions(options);
          }
        }
      } else if (
        toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN ||
        toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.SUPER_ADMIN
      ) {
        if (userListByRoleIds && userListByRoleIds?.length > 0) {
          const options = userListByRoleIds
            ?.map((item) => ({
              value: parseInt(item.userId),
              label: `${item.firstName} ${
                item.middleName ? item.middleName + " " : ""
              }${item.lastName} ${"(" + item.email + ")"}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          if (userListByRoleIds.length > 1) {
            const allLabel =
              toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.SUPER_ADMIN
                ? "All Super Admins"
                : "All Admins";
            setEntityOptions([{ value: -1, label: allLabel }, ...options]);
          } else {
            setEntityOptions(options);
          }
        }
      }
    } else if (mode == "user") {
      if (fetchUsersByQuery && fetchUsersByQuery?.length > 0) {
        const options = fetchUsersByQuery
          ?.map((item) => ({
            value: parseInt(item.userId),
            label: `${item.firstName} ${
              item.middleName ? item.middleName + " " : ""
            }${item.lastName} ${"(" + item.email + ")"}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setEntityOptions(options);
      }
    }
  }, [
    ManufacturerListInfo,
    practiceByUserRole,
    fetchUsersByQuery,
    userListByRoleIds,
    agencyInfoList,
  ]);

  useEffect(() => {
    if (entityOptions.length == 1) {
      setValue("toEntity", [entityOptions[0].value]);
      setSelectedEntity([entityOptions[0].value]);
    }
  }, [entityOptions]);

  useEffect(() => {
    if (mode === "user") {
      setSelectedEntity(toRecipients.map((r) => r.value));
    }
  }, [toRecipients, mode]);

  useEffect(() => {
    const entityValue = watch("toEntity");
    const toGroup = watch("toGroup");

    if (Array.isArray(entityValue) && entityValue.includes(-1)) {
      if (userInfo.entityName === NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN) {
        setSelectedEntity([0]);
      } else {
        if (
          toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN ||
          toGroup === NOTIFICATION_TARGET_ENTITY_NAMES.SUPER_ADMIN
        ) {
          setSelectedEntity([0]);
        } else {
          const allEntityIds = entityOptions
            .map((option) => option.value)
            .filter((value) => value !== -1);
          setSelectedEntity(allEntityIds);
        }
      }
    } else {
      setSelectedEntity(
        Array.isArray(entityValue)
          ? entityValue
          : entityValue
          ? [entityValue]
          : []
      );
    }
  }, [watch("toEntity"), entityOptions]);

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: true, autolink: true, linkOnPaste: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Type a message..." }),
    ],
    content: "",
  });

  if (!editor) return null;

  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  const MAX_FILE_SIZE_MB = 50;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);

      const validFiles: File[] = [];
      const oversizedFiles: string[] = [];

      newFiles.forEach((file) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          oversizedFiles.push(`${file.name}`);
        } else {
          validFiles.push(file);
        }
      });

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);

        const newAttachments: Attachment[] = await Promise.all(
          validFiles.map(async (file) => {
            const base64Content = await readFileAsBase64(file);
            return {
              tempId: `${file.name}-${Date.now()}-${Math.random()}`,
              documentName: file.name,
              type: "NOTIFICATION",
              content: removeDataUriPrefix(base64Content),
              fileType: file.type,
            };
          })
        );

        if (onAttachFiles) {
          await onAttachFiles(newAttachments);
        }
        setAttachments((prev) => [...prev, ...newAttachments]);
      }

      if (oversizedFiles.length > 0) {
        const alertObj: SingleAlertInfo = {
          message: `The following file(s) exceed the ${MAX_FILE_SIZE_MB}MB limit and were not attached: ${oversizedFiles.join(", ")}`,
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      }
    }
  };

  const removeFile = (indexToRemove: number) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );

    setAttachments((prev) =>
      prev.filter((_, idx) => idx !== indexToRemove)
    )
  };

  const validateSendingData = (
    selectedGroup: string,
    selectedEntity: number[]
  ) => {
    if (mode === "group") {
      if (selectedGroup === "") {
        const alertObj: SingleAlertInfo = {
          message: "Please select a Group to send the message",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        return false;
      } else {
        if (
          selectedGroup === NOTIFICATION_TARGET_ENTITY_NAMES.ADMIN ||
          selectedGroup === NOTIFICATION_TARGET_ENTITY_NAMES.SUPER_ADMIN ||
          selectedGroup === NOTIFICATION_TARGET_ENTITY_NAMES.EVERYONE
        ) {
          return true;
        } else {
          if (!selectedEntity || selectedEntity.length === 0) {
            const alertObj: SingleAlertInfo = {
              message: "Please select at least one Entity to send the message",
              alertType: "error",
            };
            dispatch(setSingleAlertObj(alertObj));
            return false;
          } else {
            return true;
          }
        }
      }
    } else {
      const ids = mode === "user" ? toRecipients.map((r) => r.value) : selectedEntity;
      if (!ids || ids.length === 0) {
        const alertObj: SingleAlertInfo = {
          message: "Please select at least one Recipient to send the message",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        return false;
      } else {
        return true;
      }
    }
  };

  const selectedToIds: number[] = selectedEntity;
  const ccExcludeIds = [
    ...selectedToIds,
    ...bccRecipients.map((r) => r.value),
  ];
  const bccExcludeIds = [
    ...selectedToIds,
    ...ccRecipients.map((r) => r.value),
  ];

  const borderColor = mTokens.border;
  const accentActive = mTokens.accent;

  return (
    <Box
      sx={{
        width: isFullscreen || isMobile ? "calc(100vw - 64px)" : 620,
        height: isFullscreen ? "calc(100vh - 50px)" : "auto",
        position: isFullscreen || isMobile ? "fixed" : "relative",
        top: isFullscreen ? 32 : "auto",
        bottom: isMobile ? 32 : "auto",
        left: isFullscreen || isMobile ? 32 : "auto",
        zIndex: isFullscreen || isMobile ? 1500 : "auto",
        backgroundColor: mTokens.bg,
        border: `1px solid ${mTokens.border}`,
        borderRadius: "16px",
        boxShadow: isDark
          ? "0 32px 80px rgba(0,0,0,0.5)"
          : "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box
        px={2.5}
        py={1.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bgcolor={mTokens.headerFooterBg}
        borderBottom={`1px solid ${borderColor}`}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {mode === "reply" ? (
            <Typography fontWeight={700} variant="h5" sx={{ color: mTokens.textPrimary }}>
              Reply to {parentNotification.senderName}
            </Typography>
          ) : (
            <Typography fontWeight={700} variant="h5" sx={{ color: mTokens.textPrimary }}>
              New message
            </Typography>
          )}
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={handleToggleFullscreen}
            sx={{ color: mTokens.textSecondary, "&:hover": { color: mTokens.textPrimary } }}
          >
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              onClose();
              onRemoveAttachments && onRemoveAttachments();
            }}
            sx={{
              ...closeIconSx,
              position: "static",
              top: "auto",
              right: "auto",
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* ── To / Group fields ─────────────────────────────────────────────── */}
      <Box px={2} mt={1}>
        {mode === "group" ? (
          <>
            <Box>
              <Box
                display="flex"
                gap={2}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <Box display="flex" alignItems="center" flex={1} gap={2}>
                  <Typography sx={{ color: mTokens.textSecondary }}>
                    To Group
                  </Typography>
                  <Box flex={1}>
                    <CustomDropdownField
                      name="toGroup"
                      control={control}
                      options={groupOptions}
                      disabled={disableGroupField}
                      rules={{ required: "Group is required." }}
                    />
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" flex={1} gap={2}>
                  <Typography sx={{ color: mTokens.textSecondary }}>
                    {entityLabel}
                  </Typography>
                  <Box flex={1}>
                    <CustomMultipleDropDown
                      name="toEntity"
                      control={control}
                      options={entityOptions}
                      disabled={
                        watch("toGroup") === "" || disableEntityField
                      }
                      sortOptions={false}
                      allOptionValue={-1}
                      rules={{
                        required: "At least one entity is required.",
                        validate: {
                          notEmpty: (value: number[]) =>
                            (value && value.length > 0) ||
                            "At least one entity is required.",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box>
            <CcBccField
              label="To"
              value={
                mode === "reply" && parentNotification
                  ? [{ value: parentNotification.senderId, label: parentNotification.senderName }]
                  : toRecipients
              }
              onChange={(val) => {
                if (mode !== "reply") setToRecipients(val);
              }}
              options={mode === "reply" ? [] : userOptions}
              excludeIds={[
                ...ccRecipients.map((r) => r.value),
                ...bccRecipients.map((r) => r.value),
              ]}
              endAdornment={
                mode === "user" ? (
                  <>
                    {!showCc && (
                      <Typography
                        variant="caption"
                        onClick={() => setShowCc(true)}
                        sx={{
                          cursor: "pointer",
                          color: mTokens.textSecondary,
                          fontWeight: "bold",
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          "&:hover": {
                            color: mTokens.accent,
                            backgroundColor: isDark
                              ? "rgba(20,184,166,0.08)"
                              : "rgba(20,184,166,0.06)",
                          },
                        }}
                      >
                        Cc
                      </Typography>
                    )}
                    {!showBcc && (
                      <Typography
                        variant="caption"
                        onClick={() => setShowBcc(true)}
                        sx={{
                          cursor: "pointer",
                          color: mTokens.textSecondary,
                          fontWeight: "bold",
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          "&:hover": {
                            color: mTokens.accent,
                            backgroundColor: isDark
                              ? "rgba(20,184,166,0.08)"
                              : "rgba(20,184,166,0.06)",
                          },
                        }}
                      >
                        Bcc
                      </Typography>
                    )}
                  </>
                ) : undefined
              }
            />

            {showCc && mode === "user" && (
              <CcBccField
                label="Cc"
                value={ccRecipients}
                onChange={setCcRecipients}
                options={userOptions}
                excludeIds={[
                  ...toRecipients.map((r) => r.value),
                  ...bccRecipients.map((r) => r.value),
                ]}
                onHide={() => setShowCc(false)}
              />
            )}

            {showBcc && mode === "user" && (
              <CcBccField
                label="Bcc"
                value={bccRecipients}
                onChange={setBccRecipients}
                options={userOptions}
                excludeIds={[
                  ...toRecipients.map((r) => r.value),
                  ...ccRecipients.map((r) => r.value),
                ]}
                onHide={() => setShowBcc(false)}
              />
            )}
          </Box>
        )}
      </Box>

      <Divider sx={{ mt: 1, mb: 1, borderColor: mTokens.border }} />

      <Grid
        px={2}
        mb={1}
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={6}>
          <TextField
            placeholder="Subject"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            value={subject}
            disabled={mode === "reply"}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <Box display="flex" alignItems="center">
            <Checkbox
              size="small"
              checked={important}
              onChange={(e) => setImportant(e.target.checked)}
              sx={{ "&.Mui-checked": { color: mTokens.accent } }}
            />
            <Typography fontSize={14} fontWeight="bold">
              Important
            </Typography>
          </Box>
        </Grid>
      </Grid>
      
      <Box
        px={2}
        py={1}
        display="flex"
        flexWrap="wrap"
        gap={0.5}
        borderTop={`1px solid ${mTokens.border}`}
        borderBottom={`1px solid ${mTokens.border}`}
        bgcolor={isDark ? mTokens.cardBg : mTokens.headerFooterBg}
      >
        <IconButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{ color: editor.isActive("bold") ? accentActive : "inherit" }}
        >
          <FormatBold />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{ color: editor.isActive("italic") ? accentActive : "inherit" }}
        >
          <FormatItalic />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          sx={{ color: editor.isActive("underline") ? accentActive : "inherit" }}
        >
          <FormatUnderlined />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          sx={{ color: editor.isActive("strike") ? accentActive : "inherit" }}
        >
          <StrikethroughS />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          sx={{ color: editor.isActive("bulletList") ? accentActive : "inherit" }}
        >
          <FormatListBulleted />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          sx={{ color: editor.isActive("orderedList") ? accentActive : "inherit" }}
        >
          <FormatListNumbered />
        </IconButton>
        <IconButton
          onClick={() =>
            editor.chain().focus().setTextAlign("left").run()
          }
          sx={{
            color: editor.isActive({ textAlign: "left" })
              ? accentActive
              : "inherit",
          }}
        >
          <FormatAlignLeft />
        </IconButton>
        <IconButton
          onClick={() =>
            editor.chain().focus().setTextAlign("center").run()
          }
          sx={{
            color: editor.isActive({ textAlign: "center" })
              ? accentActive
              : "inherit",
          }}
        >
          <FormatAlignCenter />
        </IconButton>
        <IconButton
          onClick={() =>
            editor.chain().focus().setTextAlign("right").run()
          }
          sx={{
            color: editor.isActive({ textAlign: "right" })
              ? accentActive
              : "inherit",
          }}
        >
          <FormatAlignRight />
        </IconButton>
        <IconButton
          onClick={() =>
            editor.chain().focus().setTextAlign("justify").run()
          }
          sx={{
            color: editor.isActive({ textAlign: "justify" })
              ? accentActive
              : "inherit",
          }}
        >
          <FormatAlignJustify />
        </IconButton>
        <IconButton
          onClick={() =>
            editor
              .chain()
              .focus()
              .setLink({ href: "https://google.com", target: "_blank" })
              .run()
          }
          sx={{
            color: editor.isActive("link") ? accentActive : "inherit",
          }}
        >
          <InsertLink />
        </IconButton>
      </Box>

      <Box px={2} py={2} flex={1} overflow="auto">
        <Box
          sx={{
            backgroundColor: isDark ? mTokens.cardBg : mTokens.inputBg,
            border: `1px solid ${mTokens.inputBorder}`,
            borderRadius: "10px",
            padding: 1,
            height: isFullscreen ? "100%" : 200,
            overflowY: "auto",
            "& .ProseMirror": { outline: "none", minHeight: "120px" },
            "& .ProseMirror a": {
              color:
                theme.palette.mode === "dark" ? "#82b1ff" : "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
            },
            "& .ProseMirror a:hover": {
              color:
                theme.palette.mode === "dark" ? "#bbdefb" : "#1565c0",
            },
            "& .ProseMirror a:visited": {
              color:
                theme.palette.mode === "dark" ? "#b388ff" : "#6a1b9a",
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
      
      <Box
        px={2}
        py={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderTop={`1px solid ${borderColor}`}
      >
        <Box display="flex">
          <Box>
            <IconButton
              sx={{
                color: mTokens.textSecondary,
                "&:hover": { color: mTokens.textPrimary },
              }}
              onClick={handleIconClick}
            >
              <AttachFile />
            </IconButton>
          </Box>
          <Box>
            <Tooltip title="Speech to text" placement="top" arrow>
              <IconButton
                color="default"
                component="label"
                sx={{
                  p: 1,
                  ml: 1,
                  borderRadius: 2,
                  bgcolor: mTokens.accent,
                  color: "#fff",
                  transition: "background-color 0.4s ease",
                  "&:hover": { bgcolor: mTokens.accentHover },
                }}
                onClick={() => setIsVoicePopupOpen(true)}
              >
                <KeyboardVoice />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1}>
            {attachments.map((attachment, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                sx={{
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.06)"
                    : mTokens.cardBg,
                  border: `1px solid ${mTokens.border}`,
                  borderRadius: "16px",
                  padding: "2px 2px 2px 12px",
                  fontSize: "0.775rem",
                  color: mTokens.textPrimary,
                }}
              >
                {attachment.documentName}
                <IconButton size="small" onClick={() => {removeFile(index);
                  onRemoveAttachment && onRemoveAttachment(attachment)
                }}>
                  <Close color="error" fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple
        />

        <Button
          variant="contained"
          sx={{
            ...primaryBtnSx,
            color: "#fff",
            textTransform: "none",
            "& .MuiButton-endIcon": { color: "#fff" },
          }}
          endIcon={<Send />}
          onClick={() => {
            if (validateSendingData(selectedGroup, selectedEntity)) {
              const entityIds =
                mode === "user"
                  ? toRecipients.map((r) => r.value)
                  : selectedEntity;
              onSend(
                selectedGroup,
                entityIds,
                subject,
                editor.getHTML(),
                important,
                attachments,
                parentNotification?.parentNotification
                  ? parentNotification.parentNotification.notificationId
                  : parentNotification?.notificationId ?? null,
                mode === "user" ? ccRecipients.map((r) => r.value) : undefined,
                mode === "user" ? bccRecipients.map((r) => r.value) : undefined
              );
              onClose();
            }
          }}
        >
          Send
        </Button>
      </Box>

      <SpeechToTextPopup
        open={isVoicePopupOpen}
        onClose={() => setIsVoicePopupOpen(false)}
        onSubmitVoice={(text: string) => {
          if (editor) {
            editor.commands.setContent(text);
          }
        }}
      />
    </Box>
  );
};

export default ComposePopup;
